import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useTheme,
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  CloudDownload,
  DeleteOutline,
  Tune,
  QueryStats,
  Troubleshoot,
  Analytics,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import toast from "react-hot-toast";
import api from "state/axios";
import { LAYOUT_HEIGHT } from 'state/constants';
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import ScrollToTop from "components/ScrollToTop";
import LineChart from "components/chartNivo/LineChart";
import CustomDatePicker from "components/CustomDatePicker";
import { useDelayModel, DELAY_EPOCHS } from "./useDelayModel";
import { useTurnaroundModel, TURNAROUND_EPOCHS } from "./useTurnaroundModel";
import { useAnomalyModel, ANOMALY_EPOCHS } from "./useAnomalyModel";
import PredictPanel from "./components/PredictPanel";
import FeatureAnalysis from "./components/FeatureAnalysis";
import AnomalyPanel from "./components/AnomalyPanel";

// ─── Constants ────────────────────────────────────────────────────────────────
const VIEWS = [
  { value: 'models',   label: 'Models',           icon: <Tune /> },
  { value: 'predict',  label: 'Predict',          icon: <QueryStats /> },
  { value: 'anomaly',  label: 'Anomaly Analysis', icon: <Troubleshoot /> },
  { value: 'features', label: 'Feature Analysis', icon: <Analytics /> },
];

const TRAIN_TARGETS = [
  { value: "all",        label: "All Models" },
  { value: "delay",      label: "Delay" },
  { value: "turnaround", label: "Turnaround" },
  { value: "anomaly",    label: "Anomaly" },
];

// ─── Model Status Badge ───────────────────────────────────────────────────────
const ModelBadge = ({ label, ready, training }) => (
  <Chip
    size="small"
    label={label}
    color={training ? 'warning' : ready ? 'success' : 'default'}
    variant={ready || training ? 'filled' : 'outlined'}
    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
  />
);

// ─── Model Card ───────────────────────────────────────────────────────────────
const ModelCard = ({
  label,
  isReady,
  isTraining,
  logs,
  epochs,
  onReset,
  extraChips,
  leftLegend = "Loss (MSE)",
}) => {
  const theme   = useTheme();
  const lastLog = logs[logs.length - 1];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: isTraining
          ? theme.palette.warning.main
          : isReady
          ? theme.palette.success.main
          : theme.palette.divider,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>{label}</Typography>
        <ModelBadge
          label={isTraining ? 'Training' : isReady ? 'Ready' : 'Not trained'}
          ready={isReady}
          training={isTraining}
        />
        {isReady && !isTraining && lastLog && (
          <>
            <Chip size="small" label={`Val: ${lastLog.val_loss.toFixed(5)}`} color="info" variant="outlined" />
            {lastLog.mae != null && (
              <Chip size="small" label={`MAE: ${lastLog.mae.toFixed(4)}`} color="success" variant="outlined" />
            )}
          </>
        )}
        {extraChips}
        {isReady && !isTraining && (
          <Tooltip title={`Reset ${label}`}>
            <IconButton size="small" color="error" onClick={onReset}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {isTraining && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Epoch {logs.length} / {epochs}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(logs.length / epochs) * 100}
            color="warning"
            sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
          />
        </Box>
      )}

      {logs.length > 1 && (
        <Box height={200}>
          <LineChart
            data={[
              { id: "Train",      data: logs.map((l) => ({ x: l.epoch, y: l.loss })) },
              { id: "Validation", data: logs.map((l) => ({ x: l.epoch, y: l.val_loss })) },
            ]}
            bottomLegend="Epoch"
            leftLegend={leftLegend}
            isHorizontalBottomLegend
            bottomOffSet={-95}
            bottomLegendOffSet={-35}
          />
        </Box>
      )}

      {!isReady && !isTraining && logs.length === 0 && (
        <Typography variant="body2" color="text.disabled" textAlign="center" py={2}>
          Not trained - use Load & Train above
        </Typography>
      )}
    </Paper>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const MLAnalytics = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollRef = useRef(null);

  const [view,          setView]          = useState('models');
  const [initialFlight, setInitialFlight] = useState(null);
  const [trainTarget,   setTrainTarget]   = useState("all");

  const [trainStart, setTrainStart] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d;
  });
  const [trainEnd, setTrainEnd] = useState(new Date());

  // ── Model hooks ──────────────────────────────────────────────────────────
  const {
    isModelReady: isDelayReady,
    isTraining:   isDelayTraining,
    trainingLogs: delayLogs,
    lastMetrics:  delayMetrics,
    trainModel:   trainDelay,
    predict:      predictDelay,
    resetModel:   resetDelay,
    computeFeatureImportance,
  } = useDelayModel();

  const {
    isModelReady: isTurnaroundReady,
    isTraining:   isTurnaroundTraining,
    trainingLogs: turnaroundLogs,
    lastMetrics:  turnaroundMetrics,
    trainModel:   trainTurnaround,
    predict:      predictTurnaround,
    resetModel:   resetTurnaround,
  } = useTurnaroundModel();

  const {
    isModelReady: isAnomalyReady,
    isTraining:   isAnomalyTraining,
    trainingLogs: anomalyLogs,
    trainModel:   trainAnomaly,
    predict:      predictAnomaly,
    resetModel:   resetAnomaly,
    threshold,
  } = useAnomalyModel();

  const isAnyTraining = isDelayTraining || isTurnaroundTraining || isAnomalyTraining;
  const anyModelReady = isDelayReady    || isTurnaroundReady    || isAnomalyReady;

  const trainingPhase = isDelayTraining      ? "delay"
    : isTurnaroundTraining                   ? "turnaround"
    : isAnomalyTraining                      ? "anomaly"
    : null;

  const phaseTextMap = {
    all:        { delay: "Training Delay Model (1/3)…", turnaround: "Training Turnaround Model (2/3)…", anomaly: "Training Anomaly Model (3/3)…" },
    delay:      { delay: "Training Delay Model…" },
    turnaround: { turnaround: "Training Turnaround Model…" },
    anomaly:    { anomaly: "Training Anomaly Model…" },
  };
  const phaseText = trainingPhase ? phaseTextMap[trainTarget]?.[trainingPhase] : null;

  const readyCount = [isDelayReady, isTurnaroundReady, isAnomalyReady].filter(Boolean).length;
  const subtitle   = isAnyTraining  ? phaseText ?? 'Training…'
    : readyCount === 3              ? 'All models ready'
    : readyCount > 0                ? `${readyCount}/3 models ready`
    : 'Load & train models';

  useEffect(() => {
    if (location.state?.selectedFlight) {
      setInitialFlight(location.state.selectedFlight);
      if (isDelayReady || isTurnaroundReady) setView('predict');
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state?.selectedFlight]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((isDelayReady || isTurnaroundReady) && initialFlight) setView('predict');
  }, [isDelayReady, isTurnaroundReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTrain = async () => {
    const toastId = toast.loading("Fetching training data…");
    try {
      const body = {};
      if (trainStart) body.startDate = trainStart.toISOString();
      if (trainEnd)   body.endDate   = trainEnd.toISOString();

      const { delay, turnaround } = (await api.post("/flights/ml-data", body)).data;

      if (trainTarget === "all") {
        const { data: fd, total: t1 } = delay;
        const { data: ta, total: t2 } = turnaround;
        if (!fd || t1 < 10) { toast.error("Not enough flight data (need ≥ 10).", { id: toastId }); return; }
        toast.loading(`${t1} records, ${t2 ?? 0} pairs - training…`, { id: toastId });
        const delaySaved = await trainDelay(fd);
        let turnaroundSaved = true;
        if (ta && t2 >= 10) {
          toast.loading("Delay done - turnaround…", { id: toastId });
          turnaroundSaved = await trainTurnaround(ta);
          toast.loading("Turnaround done - anomaly…", { id: toastId });
        } else {
          toast.loading("Delay done - turnaround skipped (< 10 pairs) - anomaly…", { id: toastId });
        }
        const anomalySaved = await trainAnomaly(fd);
        if (delaySaved && turnaroundSaved && anomalySaved) {
          toast.success("All models trained.", { id: toastId });
        } else {
          toast("All models trained. Note: could not save to IndexedDB - retrain after page refresh.", { id: toastId, duration: 8000 });
        }

      } else if (trainTarget === "delay") {
        const { data, total } = delay;
        if (!data || total < 10) { toast.error("Not enough data (need ≥ 10).", { id: toastId }); return; }
        toast.loading(`${total} records - training delay…`, { id: toastId });
        const saved = await trainDelay(data);
        toast.success(`Delay model trained.${saved ? '' : ' (IndexedDB save failed)'}`, { id: toastId });

      } else if (trainTarget === "turnaround") {
        const { data, total } = turnaround;
        if (!data || total < 10) { toast.error("Not enough pairs (need ≥ 10).", { id: toastId }); return; }
        toast.loading(`${total} pairs - training turnaround…`, { id: toastId });
        await trainTurnaround(data);
        toast.success("Turnaround model trained.", { id: toastId });

      } else if (trainTarget === "anomaly") {
        const { data, total } = delay;
        if (!data || total < 10) { toast.error("Not enough data (need ≥ 10).", { id: toastId }); return; }
        toast.loading(`${total} records - training anomaly…`, { id: toastId });
        await trainAnomaly(data);
        toast.success("Anomaly model trained.", { id: toastId });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message, { id: toastId });
    }
  };

  const handleResetAll = async () => {
    await Promise.all([resetDelay(), resetTurnaround(), resetAnomaly()]);
    setInitialFlight(null);
    setView('models');
    toast.success("All models cleared.");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
    <Box className="main-container-box">
      {/* ── Header row ── */}
      <FlexBetween>
        <Header 
          title="ML Analytics" 
          subtitle={subtitle} 
        />
        <Box 
          display="flex" 
          flexWrap="wrap" 
          alignItems="center" 
          justifyContent="flex-end"
          gap={1} 
        >
          <Stack direction="row" spacing={0.5}>
            <ModelBadge label="Delay"     ready={isDelayReady}      training={isDelayTraining} />
            <ModelBadge label="TAT"       ready={isTurnaroundReady} training={isTurnaroundTraining} />
            <ModelBadge label="Anomaly"   ready={isAnomalyReady}    training={isAnomalyTraining} />
          </Stack>

          <ToggleButtonGroup
            aria-label="View"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
          >
            {VIEWS.map(({ value, label, icon }) => (
              <Tooltip key={value} title={label} arrow>
                <span>
                  <ToggleButton
                    size="small"
                    value={value}
                    aria-label={label}
                    disabled={
                      isAnyTraining ||
                      (value === 'predict'  && !isDelayReady && !isTurnaroundReady) ||
                      (value === 'anomaly'  && !isAnomalyReady) ||
                      (value === 'features' && !isDelayReady)
                    }
                  >
                    {icon}
                  </ToggleButton>
                </span>
              </Tooltip>
            ))}
          </ToggleButtonGroup>
        </Box>
      </FlexBetween>
      
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        gap={2}
      >
        <Box
          ref={scrollRef} 
          height={LAYOUT_HEIGHT}
          sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
          {/* ── Models view ── */}
          {view === 'models' && (
            <Box 
              display="flex" 
              flexDirection="column" 
              gap={2} 
              pt={1}
            >
              {/* Training config bar */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                flexWrap="wrap"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
              >
                <CustomDatePicker
                  variant="outlined"
                  size="small"
                  label="From Date"
                  name="trainStart"
                  value={trainStart}
                  maxDate={trainEnd || new Date()}
                  onChange={(_, v) => setTrainStart(v)}
                  sx={{ width: { xs: '100%', sm: 160 } }}
                />
                <CustomDatePicker
                  label="To Date"
                  name="trainEnd"
                  value={trainEnd}
                  onChange={(_, v) => setTrainEnd(v)}
                  minDate={trainStart}
                  maxDate={new Date()}
                  size="small"
                  variant="outlined"
                  sx={{ width: { xs: '100%', sm: 160 } }}
                />
                <FormControl 
                  size="small" 
                  sx={{ minWidth: 140, width: { xs: '100%', sm: 'auto' } }}
                >
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={trainTarget}
                    label="Model"
                    disabled={isAnyTraining}
                    onChange={(e) => setTrainTarget(e.target.value)}
                  >
                    {TRAIN_TARGETS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<CloudDownload />}
                  disabled={isAnyTraining}
                  onClick={handleTrain}
                  sx={{ whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
                >
                  {isAnyTraining ? 'Training…' : 'Load & Train'}
                </Button>
                {anyModelReady && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutline />}
                    disabled={isAnyTraining}
                    onClick={handleResetAll}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Reset All
                  </Button>
                )}
              </Stack>

              {/* Phase progress */}
              {phaseText && (
                <Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    mb={1}
                  >
                    {phaseText}
                  </Typography>
                  <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
                </Box>
              )}

              {/* Model cards */}
              <Box
                flex={1}
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: 'repeat(1, 1fr)' }}
                gap={2}
              >
                <ModelCard
                  label="Delay Model"
                  isReady={isDelayReady}
                  isTraining={isDelayTraining}
                  logs={delayLogs}
                  epochs={DELAY_EPOCHS}
                  onReset={async () => {
                    await resetDelay();
                    setInitialFlight(null);
                    toast.success("Delay model cleared.");
                  }}
                  extraChips={delayMetrics && !isDelayTraining ? (
                    <>
                      <Chip size="small" label={`Val: ${delayMetrics.val_loss.toFixed(5)}`} color="info" variant="outlined" />
                      {delayMetrics.mae != null && (
                        <Chip size="small" label={`MAE: ${delayMetrics.mae.toFixed(4)}`} color="success" variant="outlined" />
                      )}
                    </>
                  ) : null}
                />
                <ModelCard
                  label="Turnaround Model"
                  isReady={isTurnaroundReady}
                  isTraining={isTurnaroundTraining}
                  logs={turnaroundLogs}
                  epochs={TURNAROUND_EPOCHS}
                  onReset={() => { resetTurnaround(); toast.success("Turnaround model cleared."); }}
                  extraChips={turnaroundMetrics && !isTurnaroundTraining ? (
                    <>
                      <Chip size="small" label={`Val: ${turnaroundMetrics.val_loss.toFixed(5)}`} color="info" variant="outlined" />
                      {turnaroundMetrics.mae != null && (
                        <Chip size="small" label={`MAE: ${turnaroundMetrics.mae.toFixed(4)}`} color="success" variant="outlined" />
                      )}
                    </>
                  ) : null}
                />
                <ModelCard
                  label="Anomaly Model"
                  isReady={isAnomalyReady}
                  isTraining={isAnomalyTraining}
                  logs={anomalyLogs}
                  epochs={ANOMALY_EPOCHS}
                  leftLegend="Reconstruction Loss (MSE)"
                  onReset={() => { resetAnomaly(); toast.success("Anomaly model cleared."); }}
                  extraChips={threshold !== null && !isAnomalyTraining
                    ? <Chip size="small" label={`Threshold: ${(threshold * 1000).toFixed(3)}‰`} color="info" variant="outlined" />
                    : null
                  }
                />
              </Box>
            </Box>
          )}

          {/* ── Predict view ── */}
          {view === 'predict' && (
            <PredictPanel
              predictDelay={predictDelay}
              predictTurnaround={predictTurnaround}
              isDelayReady={isDelayReady}
              isTurnaroundReady={isTurnaroundReady}
              initialFlight={initialFlight}
            />
          )}

          {/* ── Anomaly view ── */}
          {view === 'anomaly' && (
            <Box height="100%">
              <AnomalyPanel
                hideTraining
                isModelReady={isAnomalyReady}
                isTraining={isAnomalyTraining}
                threshold={threshold}
                predictAnomaly={predictAnomaly}
              />
            </Box>
          )}

          {/* ── Features view ── */}
          {view === 'features' && (
            <Box height="100%" overflow="hidden">
              <FeatureAnalysis
                computeFeatureImportance={computeFeatureImportance}
              />
            </Box>
          )}

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>
    </Box>
    </LocalizationProvider>
  );
};

export default MLAnalytics;
